import { FormEvent, useEffect, useState } from 'react';
import { createStore, listStores, StoreSummary } from '../../api/admin';
import PaymentCredentialsPanel from './PaymentCredentialsPanel';
import PolicyPanel from './PolicyPanel';
import './StoreManagementScreen.scss';

const EMPTY_FORM = { name: '', bundleId: '', adminEmail: '', adminPassword: '' };

function StoreManagementScreen() {
  const [stores, setStores] = useState<StoreSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [paymentStore, setPaymentStore] = useState<StoreSummary | null>(null);
  const [policyStore, setPolicyStore] = useState<StoreSummary | null>(null);

  const loadStores = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const rows = await listStores();
      setStores(rows);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStores();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await createStore(form);
      setForm(EMPTY_FORM);
      await loadStores();
    } catch (err: any) {
      setError(err.response?.data?.message || '매장 생성에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="StoreManagementScreen">
      <section className="StoreManagementScreen-panel">
        <h2>새 매장 추가</h2>
        <form className="StoreManagementScreen-form" onSubmit={handleSubmit}>
          <label>
            매장명
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </label>
          <label>
            앱 번들 ID
            <input
              value={form.bundleId}
              placeholder="com.cafe.cafeireh"
              onChange={(e) => setForm({ ...form, bundleId: e.target.value })}
              required
            />
          </label>
          <label>
            매장 관리자 이메일
            <input
              type="email"
              value={form.adminEmail}
              onChange={(e) => setForm({ ...form, adminEmail: e.target.value })}
              required
            />
          </label>
          <label>
            매장 관리자 비밀번호
            <input
              type="password"
              value={form.adminPassword}
              onChange={(e) => setForm({ ...form, adminPassword: e.target.value })}
              required
            />
          </label>
          {error && <p className="StoreManagementScreen-error">{error}</p>}
          <button type="submit" disabled={submitting}>
            {submitting ? '생성 중...' : '매장 생성'}
          </button>
        </form>
      </section>

      <section className="StoreManagementScreen-panel">
        <h2>전체 매장 목록</h2>
        {loading ? (
          <p>불러오는 중...</p>
        ) : (
          <table className="StoreManagementScreen-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>매장명</th>
                <th>앱 번들 ID</th>
                <th>관리자 이메일</th>
                <th>생성일</th>
                <th>결제</th>
                <th>약관</th>
              </tr>
            </thead>
            <tbody>
              {stores.map((store) => (
                <tr key={store.id}>
                  <td>{store.id}</td>
                  <td>{store.name}</td>
                  <td>{store.bundle_id}</td>
                  <td>{store.admin_emails || '-'}</td>
                  <td>{new Date(store.created_at).toLocaleDateString()}</td>
                  <td>
                    <span className={`StoreManagementScreen-badge mode ${store.payment_mode}`}>
                      {store.payment_mode === 'production' ? '운영' : '샌드박스'}
                    </span>
                    <span className={`StoreManagementScreen-badge ${store.kakao_configured ? 'on' : ''}`}>카카오</span>
                    <span className={`StoreManagementScreen-badge ${store.naver_configured ? 'on' : ''}`}>네이버</span>
                    <button
                      type="button"
                      className="StoreManagementScreen-linkButton"
                      onClick={() => setPaymentStore(store)}
                    >
                      설정
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="StoreManagementScreen-linkButton"
                      onClick={() => setPolicyStore(store)}
                    >
                      관리
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {paymentStore && (
        <PaymentCredentialsPanel
          store={paymentStore}
          onClose={() => setPaymentStore(null)}
          onSaved={() => loadStores(false)}
        />
      )}

      {policyStore && <PolicyPanel store={policyStore} onClose={() => setPolicyStore(null)} />}
    </div>
  );
}

export default StoreManagementScreen;
